import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getTimezoneOffsetLabel,
  isValidTimezone,
  listTimezones,
} from "../../utils/scheduleTime";

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Small caption above the control, e.g. "Business profile timezone". */
  label?: string;
}

type ZoneOption = {
  zone: string;
  city: string;
  region: string;
  offset: string;
  /** Pre-lowercased haystack so filtering never re-derives it per keystroke. */
  search: string;
};

/** "America/Chicago" → "Chicago", "America/New_York" → "New York". */
const cityOf = (zone: string): string =>
  (zone.split("/").pop() ?? zone).replace(/_/g, " ");

const regionOf = (zone: string): string =>
  zone.includes("/") ? zone.split("/")[0] : "Other";

/**
 * Searchable timezone picker.
 *
 * Was a native <select>; ~400 options made browser type-ahead impractical, so
 * this is a filter input over a listbox. Deliberately hand-rolled rather than
 * adding a combobox library — this repo already carries four UI libraries and a
 * fifth for one control would not earn its keep.
 *
 * Options are labelled with the zone's CURRENT offset ("Chicago · GMT-05:00"),
 * computed via Intl so it follows DST rather than freezing whatever offset
 * applied when the page loaded. Typing matches city, region, full zone id or
 * offset, so "chicago", "america" and "gmt-05" all reach America/Chicago.
 */
const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  disabled = false,
  id,
  className = "",
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const options = useMemo<ZoneOption[]>(() => {
    const zones = listTimezones();

    // A stored value that is not in the browser's list (a legacy row, or a zone
    // this engine does not enumerate) must still appear, or opening the picker
    // would silently rewrite the post's timezone to whatever lands first.
    const all =
      value && isValidTimezone(value) && !zones.includes(value)
        ? [value, ...zones]
        : zones;

    return all
      .map((zone) => {
        const city = cityOf(zone);
        const region = regionOf(zone);
        const offset = getTimezoneOffsetLabel(zone);
        return {
          zone,
          city,
          region,
          offset,
          search: `${zone} ${city} ${region} ${offset}`.toLowerCase(),
        };
      })
      .sort(
        (a, b) =>
          a.region.localeCompare(b.region) || a.city.localeCompare(b.city)
      );
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Every whitespace-separated term must match, so "america chicago" narrows
    // the list rather than widening it the way one substring match would.
    const terms = q.split(/\s+/);
    return options.filter((o) => terms.every((t) => o.search.includes(t)));
  }, [options, query]);

  const selected = useMemo(
    () => options.find((o) => o.zone === value),
    [options, value]
  );

  const selectedLabel = selected
    ? `${selected.city} · ${selected.offset}`
    : value || "Select timezone";

  // Highlight the current value when the list opens, and fall back to the top
  // row whenever a query narrows the list out from under it.
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.zone === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, filtered, value]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Keep the highlighted row inside the scroll viewport during keyboard nav.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    // Guarded: jsdom (and some older engines) do not implement scrollIntoView,
    // and losing the scroll nicety must never take the whole picker down.
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    // Focus after paint, once the input actually exists to receive it.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = (zone: string) => {
    onChange(zone);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[activeIndex];
      if (pick) commit(pick.zone);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  // Region headers are emitted inline off the flat list so `activeIndex` stays
  // aligned with `filtered` and arrow keys never land on a header row.
  let lastRegion = "";

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label && (
        <label
          htmlFor={id}
          className="mb-1 block text-xs font-medium text-theme-fg-muted"
        >
          {label}
        </label>
      )}

      {open ? (
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={id ? `${id}-listbox` : undefined}
          autoComplete="off"
          value={query}
          placeholder={`Search — ${selectedLabel}`}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full rounded-md border border-theme-primary bg-theme-surface px-2 py-1.5 text-xs text-theme-fg outline-none"
        />
      ) : (
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={openList}
          onKeyDown={handleKeyDown}
          className="flex w-full items-center justify-between rounded-md border border-theme-border bg-theme-surface px-2 py-1.5 text-left text-xs text-theme-fg outline-none focus:border-theme-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="truncate">{selectedLabel}</span>
          <span className="material-icons ml-1 shrink-0 text-sm opacity-60">
            expand_more
          </span>
        </button>
      )}

      {open && (
        <div
          ref={listRef}
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-theme-border bg-theme-surface shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-theme-fg-muted">
              No timezone matches that search
            </div>
          ) : (
            filtered.map((o, index) => {
              const showHeader = o.region !== lastRegion;
              lastRegion = o.region;
              return (
                <React.Fragment key={o.zone}>
                  {showHeader && (
                    <div className="sticky top-0 bg-theme-background px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
                      {o.region}
                    </div>
                  )}
                  <div
                    data-index={index}
                    role="option"
                    aria-selected={o.zone === value}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(o.zone)}
                    className={`cursor-pointer px-2 py-1.5 text-xs text-theme-fg ${
                      index === activeIndex ? "bg-theme-primary/10" : ""
                    } ${o.zone === value ? "font-semibold" : ""}`}
                  >
                    {o.city}
                    <span className="ml-1 opacity-60">· {o.offset}</span>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default TimezoneSelect;
